import {
  Alert,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Input,
  List,
  ListItem,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ProjectType_Entity } from "../../../entities/projectType";
import { partitionArchiveSelection } from "../../../helpers/archiveSelection";
import { useUpdateProjectTypeMutation } from "../../../redux/projectType/projectTypeEndpoints";
import { useProjectTypes } from "../../../redux/projectType/projectTypeHooks";

/**
 * Archive rate columns, in bulk.
 *
 * Retiring a rate card one row at a time from the Project Types admin page is
 * the slow path — the columns pile up faster than anyone visits that page,
 * which is why the stale ones ended up renamed "OLD ..." instead. This is the
 * quick path, from the grid where the clutter is actually visible: pick the
 * columns, review what will happen, apply in one go.
 *
 * @param {object} props open/onClose
 * @returns {ReactElement} the dialog
 */
export default function ArchiveColumnsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { projectTypes } = useProjectTypes();
  const [updateProjectType] = useUpdateProjectTypeMutation();

  const [step, setStep] = useState<"select" | "review">("select");
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  // Reopening should never inherit the last run's selection — that would make
  // it far too easy to archive a column nobody meant to touch.
  useEffect(() => {
    if (open) {
      setStep("select");
      setSelected([]);
      setSearch("");
      setSaving(false);
      setFailed(false);
    }
  }, [open]);

  const sorted = useMemo(
    () =>
      [...(projectTypes ?? [])].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "")
      ),
    [projectTypes]
  );

  const matches = (pt: ProjectType_Entity) =>
    (pt.name ?? "").toLowerCase().includes(search.trim().toLowerCase());

  const visible = sorted.filter((pt) => !pt.archived && matches(pt));
  const archived = sorted.filter((pt) => pt.archived && matches(pt));

  const { toArchive, toRestore } = useMemo(
    () => partitionArchiveSelection(sorted, selected),
    [sorted, selected]
  );

  const toggle = (id: number) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );

  // Bulk select per section, honouring the search box — typing "OLD" and hitting
  // Select all is the whole point, given how the stale columns got named.
  const allSelectedIn = (group: ProjectType_Entity[]) =>
    group.length > 0 && group.every((pt) => selected.includes(pt.id));

  const toggleGroup = (group: ProjectType_Entity[]) =>
    setSelected((prev) => {
      const ids = group.map((pt) => pt.id);
      return allSelectedIn(group)
        ? prev.filter((id) => !ids.includes(id))
        : [...prev, ...ids.filter((id) => !prev.includes(id))];
    });

  const apply = async () => {
    setSaving(true);
    setFailed(false);
    try {
      await Promise.all([
        ...toArchive.map((pt) =>
          updateProjectType({ id: pt.id, body: { archived: true } }).unwrap()
        ),
        ...toRestore.map((pt) =>
          updateProjectType({ id: pt.id, body: { archived: false } }).unwrap()
        ),
      ]);
      onClose();
    } catch (error) {
      // Some of the batch may have landed; the grid refetch shows exactly which,
      // so leave the dialog open on the review step rather than guessing.
      console.error("Archiving rate columns failed", error);
      setFailed(true);
      setSaving(false);
    }
  };

  const renderRow = (pt: ProjectType_Entity) => (
    <ListItem key={pt.id}>
      <Checkbox
        size="sm"
        label={pt.name}
        checked={selected.includes(pt.id)}
        onChange={() => toggle(pt.id)}
      />
    </ListItem>
  );

  const renderSection = (
    title: string,
    group: ProjectType_Entity[],
    emptyText: string
  ) => (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pt: 1,
        }}
      >
        <Typography level="body3" sx={{ fontWeight: "bold" }}>
          {title} ({group.length})
        </Typography>
        {group.length ? (
          <Button
            size="sm"
            variant="plain"
            onClick={() => toggleGroup(group)}
            sx={{ minHeight: 0, py: 0.25 }}
          >
            {allSelectedIn(group) ? "Clear" : "Select all"}
          </Button>
        ) : (
          []
        )}
      </Box>
      {group.length ? (
        <List size="sm">{group.map(renderRow)}</List>
      ) : (
        <Typography level="body3" sx={{ py: 0.5 }}>
          {emptyText}
        </Typography>
      )}
    </>
  );

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 480, maxWidth: "95vw" }}>
        {!saving ? <ModalClose onClick={onClose} /> : []}
        <DialogTitle>
          {step === "select" ? "Archive & restore columns" : "Confirm changes"}
        </DialogTitle>

        {step === "select" ? (
          <DialogContent sx={{ gap: 1.5 }}>
            <Typography level="body2">
              Tick a column to archive it, or tick an archived one to bring it
              back. Archived columns drop out of the Rates grid and the project
              type picker; projects already using them keep their rates.
            </Typography>

            <Input
              size="sm"
              placeholder="Search columns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Box sx={{ maxHeight: "45vh", overflow: "auto" }}>
              {renderSection(
                "Showing on Rates",
                visible,
                "No matching columns."
              )}
              <Divider sx={{ my: 1 }} />
              {/* Always rendered, even when empty — this is the only place to
                  restore a column, so it has to be visible to be found. */}
              {renderSection(
                "Archived",
                archived,
                "Nothing archived yet — archived columns show up here to restore."
              )}
            </Box>
          </DialogContent>
        ) : (
          <DialogContent sx={{ gap: 1.5 }}>
            {toArchive.length ? (
              <Box>
                <Typography level="body2" sx={{ fontWeight: "bold" }}>
                  Archiving {toArchive.length}{" "}
                  {toArchive.length === 1 ? "column" : "columns"}
                </Typography>
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 1 }}
                >
                  {toArchive.map((pt) => (
                    <Chip key={pt.id} size="sm" variant="soft">
                      {pt.name}
                    </Chip>
                  ))}
                </Box>
              </Box>
            ) : (
              []
            )}

            {toRestore.length ? (
              <Box>
                <Typography level="body2" sx={{ fontWeight: "bold" }}>
                  Restoring {toRestore.length}{" "}
                  {toRestore.length === 1 ? "column" : "columns"}
                </Typography>
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 1 }}
                >
                  {toRestore.map((pt) => (
                    <Chip key={pt.id} size="sm" variant="soft">
                      {pt.name}
                    </Chip>
                  ))}
                </Box>
              </Box>
            ) : (
              []
            )}

            <Typography level="body3">
              You can undo this at any time — reopen this dialog and tick them
              under &quot;Archived&quot; to put them back.
            </Typography>

            {failed ? (
              <Alert color="danger" size="sm">
                Some changes could not be saved. Close and check the grid before
                trying again.
              </Alert>
            ) : (
              []
            )}
          </DialogContent>
        )}

        <DialogActions>
          {step === "select" ? (
            <>
              <Button
                size="sm"
                onClick={() => setStep("review")}
                disabled={!selected.length}
              >
                Continue
                {selected.length ? ` (${selected.length})` : ""}
              </Button>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={onClose}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={apply} loading={saving}>
                Apply
              </Button>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setStep("select")}
                disabled={saving}
              >
                Back
              </Button>
            </>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
